/**
 * General Application Constants
 *
 * Configuration values, enums, and other app-wide constants.
 */

/**
 * Application Constants
 */
export const APP_CONSTANTS = {
    AUTHENTICATED: 'authenticated',
    AUTH_TOKEN: 'auth_token',
    LOGOUT: 'logout',
    LOGINDATA: 'authenticated',
    LOAN_DATA: 'loan_data',
    USER_DATA: 'user_data',
    THEME: 'theme',
    LANGUAGE: 'language',
    branchId: 'branchId',
    branch: 'branch',
    USER_MENU:'user_menu',
    skipLoaderUrls: ['/auth/refresh', '/status/ping'],
      
      // URLs to skip token attachment
       skipTokenUrls: ['/auth/login', '/auth/register', '/otp/verify'],
} as const;

/**
 * User Roles
 */
export const USER_ROLES = {
  CUSTOMER: 'customer',
  ADMIN: 'admin',
  MANAGER: 'manager',
  COLLECTION_AGENT: 'collection-agent'
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

/**
 * Loan Status
 */
export const LOAN_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ACTIVE: 'active',
  CLOSED: 'closed',
  OVERDUE: 'overdue',
  DEFAULTED: 'defaulted'
} as const;

export type LoanStatus = typeof LOAN_STATUS[keyof typeof LOAN_STATUS];

/**
 * Application Status
 */
export const APPLICATION_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under-review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled'
} as const;

export type ApplicationStatus = typeof APPLICATION_STATUS[keyof typeof APPLICATION_STATUS];

/**
 * Payment Status
 */
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded'
} as const;

export type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];

/**
 * Payment Methods
 */
export const PAYMENT_METHODS = {
  CREDIT_CARD: 'credit-card',
  DEBIT_CARD: 'debit-card',
  BANK_TRANSFER: 'bank-transfer',
  UPI: 'upi',
  CASH: 'cash',
  CHEQUE: 'cheque'
} as const;

export type PaymentMethod = typeof PAYMENT_METHODS[keyof typeof PAYMENT_METHODS];

/**
 * Document Types
 */
export const DOCUMENT_TYPES = {
  ID_PROOF: 'id-proof',
  ADDRESS_PROOF: 'address-proof',
  INCOME_PROOF: 'income-proof',
  BANK_STATEMENT: 'bank-statement',
  VEHICLE_REGISTRATION: 'vehicle-registration',
  INSURANCE: 'insurance',
  OTHER: 'other'
} as const;

export type DocumentType = typeof DOCUMENT_TYPES[keyof typeof DOCUMENT_TYPES];

/**
 * Document Status
 */
export const DOCUMENT_STATUS = {
  PENDING: 'pending',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
  EXPIRED: 'expired'
} as const;

export type DocumentStatus = typeof DOCUMENT_STATUS[keyof typeof DOCUMENT_STATUS];

/**
 * Notification Types
 */
export const NOTIFICATION_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  PAYMENT_DUE: 'payment-due',
  PAYMENT_RECEIVED: 'payment-received',
  LOAN_APPROVED: 'loan-approved',
  LOAN_REJECTED: 'loan-rejected',
  APPLICATION_STATUS: 'application-status',
  SYSTEM: 'system'
} as const;

export type NotificationType = typeof NOTIFICATION_TYPES[keyof typeof NOTIFICATION_TYPES];

/**
 * Loan Types
 */
export const LOAN_TYPES = {
  NEW_CAR: 'new-car',
  USED_CAR: 'used-car',
  REFINANCE: 'refinance',
  LEASE: 'lease'
} as const;

export type LoanType = typeof LOAN_TYPES[keyof typeof LOAN_TYPES];

/**
 * Storage Keys for Local/Session Storage
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  USER_ROLE: 'user_role',
  REMEMBER_ME: 'remember_me',
  THEME: 'theme',
  LANGUAGE: 'language',
  BIOMETRIC_ENABLED: 'biometric_enabled',
  LAST_LOGIN: 'last_login',
  APP_VERSION: 'app_version'
} as const;

/**
 * Date/Time Formats
 */
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_LONG: 'MMMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  API: 'YYYY-MM-DD',
  API_WITH_TIME: 'YYYY-MM-DD HH:mm:ss',
  TIME_ONLY: 'HH:mm',
  TIME_12H: 'hh:mm A'
} as const;

/**
 * Pagination
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGE_SIZE: 100
} as const;

/**
 * File Upload Limits
 */
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_SIZE_MB: 5,
  ALLOWED_TYPES: {
    IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
    DOCUMENTS: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    ALL: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  },
  ALLOWED_EXTENSIONS: {
    IMAGES: ['.jpg', '.jpeg', '.png', '.gif'],
    DOCUMENTS: ['.pdf', '.doc', '.docx'],
    ALL: ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx']
  }
} as const;

/**
 * Regex Patterns for Validation
 */
export const REGEX_PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
  NUMERIC: /^[0-9]+$/,
  ALPHABETIC: /^[a-zA-Z]+$/,
  URL: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
  ZIP_CODE: /^\d{5}(-\d{4})?$/
} as const;

/**
 * App Configuration
 */
export const APP_CONFIG = {
  // App Info
  APP_NAME: 'Car Loan Finance',
  VERSION: '1.0.0',
  BUILD_NUMBER: '1',
  
  // Timeouts
  DEFAULT_TIMEOUT: 30000, // 30 seconds
  SHORT_TIMEOUT: 10000,   // 10 seconds
  LONG_TIMEOUT: 60000,    // 60 seconds
  
  // Retry Configuration
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second
  
  // Cache Duration (in milliseconds)
  CACHE_DURATION: {
    SHORT: 5 * 60 * 1000,      // 5 minutes
    MEDIUM: 30 * 60 * 1000,    // 30 minutes
    LONG: 24 * 60 * 60 * 1000  // 24 hours
  },
  
  // Session Configuration
  SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes
  SESSION_WARNING_TIME: 5 * 60 * 1000, // 5 minutes before timeout
  
  // Debounce/Throttle Delays
  SEARCH_DEBOUNCE: 300,  // 300ms
  SCROLL_THROTTLE: 100,  // 100ms
  RESIZE_THROTTLE: 200,  // 200ms
  
  // Animation Durations
  ANIMATION: {
    FAST: 200,
    NORMAL: 300,
    SLOW: 500
  }
} as const;

/**
 * Icon Names (Ionicons)
 */
export const ICONS = {
  // Navigation
  HOME: 'home-outline',
  DASHBOARD: 'grid-outline',
  MENU: 'menu-outline',
  BACK: 'arrow-back-outline',
  FORWARD: 'arrow-forward-outline',
  CLOSE: 'close-outline',
  
  // Actions
  ADD: 'add-outline',
  EDIT: 'create-outline',
  DELETE: 'trash-outline',
  SAVE: 'checkmark-outline',
  CANCEL: 'close-circle-outline',
  SEARCH: 'search-outline',
  FILTER: 'funnel-outline',
  REFRESH: 'refresh-outline',
  DOWNLOAD: 'download-outline',
  UPLOAD: 'cloud-upload-outline',
  SHARE: 'share-social-outline',
  
  // User
  PERSON: 'person-outline',
  MAIL: 'mail-outline',
  LOCK: 'lock-closed-outline',
  LOCK_OPEN: 'lock-open-outline',
  EYE: 'eye-outline',
  EYE_OFF: 'eye-off-outline',
  FINGERPRINT: 'finger-print-outline',
  
  // Status
  SUCCESS: 'checkmark-circle-outline',
  ERROR: 'close-circle-outline',
  WARNING: 'warning-outline',
  INFO: 'information-circle-outline',
  
  // Loan/Finance
  CARD: 'card-outline',
  CASH: 'cash-outline',
  WALLET: 'wallet-outline',
  DOCUMENT: 'document-text-outline',
  CALENDAR: 'calendar-outline',
  TIME: 'time-outline',
  
  // Settings
  SETTINGS: 'settings-outline',
  NOTIFICATIONS: 'notifications-outline',
  HELP: 'help-circle-outline',
  LOGOUT: 'log-out-outline',
  
  // Communication
  CALL: 'call-outline',
  CHAT: 'chatbubble-outline',
  SEND: 'send-outline',
  
  // Other
  STAR: 'star-outline',
  HEART: 'heart-outline',
  LOCATION: 'location-outline',
  CAMERA: 'camera-outline',
  IMAGE: 'image-outline',
  ATTACH: 'attach-outline'
} as const;

/**
 * Color Palette (for charts, status indicators, etc.)
 */
export const COLORS = {
  PRIMARY: '#10b981',
  SECONDARY: '#3b82f6',
  SUCCESS: '#10b981',
  WARNING: '#f59e0b',
  ERROR: '#ef4444',
  INFO: '#3b82f6',
  
  // Chart Colors
  CHART: [
    '#10b981', // green
    '#3b82f6', // blue
    '#8b5cf6', // purple
    '#f59e0b', // orange
    '#ef4444', // red
    '#06b6d4', // cyan
    '#ec4899', // pink
    '#84cc16'  // lime
  ]
} as const;

/**
 * Environment flags
 */
export const ENVIRONMENT = {
  PRODUCTION: false,
  DEVELOPMENT: true,
  DEBUG: true
} as const;
